import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, ButtonGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { addGuestDraft } from '../hooks/useGuestSaves';
import useMealTypes from '../hooks/useMealTypes';
import { toLocalDatetimeInputValue } from '../utils/datetime';
import { prepJpeg } from '../utils/imagePrep';
import { uploadVisitBlob } from '../utils/visitPhotos';
import { saveGuestPhotos } from '../utils/guestPhotoStore';
import { locationLabel } from '../utils/locations';
import VisitDishRow from './VisitDishRow';

// A fresh, empty dish row for the multi-dish visit picker (#110).
let _dishUid = 0;
const makeDish = (patch = {}) => ({
  uid: `d${++_dishUid}`,
  menuItemId: '',
  dishNameText: '',
  matchedFood: null,
  rating: 0,
  photos: [],   // #115: [{ file, previewUrl }] — photos OF this dish
  ...patch,
});

const MAX_VISIT_PHOTOS = 5;

function ReviewForm({ restaurantId, foodId, menuItemId, targetName, onReviewSubmitted, visitOnly = false, menuItems = [],
                      restaurantVisibility, restaurantReviewState }) {
  // #171: a restaurant kept private "just for me" has no public page, so a public
  // review of it can never be seen by anyone — the backend now filters such
  // reviews out of every public listing, and the form shouldn't offer to create
  // one in the first place. A PENDING submission is different: it's on its way to
  // being public, so sharing stays offered and goes live on approval.
  const isPrivateForever = restaurantVisibility === 'private' && restaurantReviewState !== 'pending';
  const isAwaitingApproval = restaurantReviewState === 'pending';
  // When visitOnly (the user has already reviewed this target), the review mode
  // is suppressed and only visit logging is offered — see #38.
  const [mode, setMode] = useState(visitOnly ? 'visit' : 'review'); // 'review' or 'visit'
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [visitDate, setVisitDate] = useState(toLocalDatetimeInputValue());
  const [mealType, setMealType] = useState('');
  const mealTypes = useMealTypes();
  // Which physical location the visit was at (#41). Only offered for multi-location
  // restaurants; single-location restaurants are auto-filled server-side, and an
  // empty value posts as restaurant-wide (NULL).
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [createPublicReview, setCreatePublicReview] = useState(false);
  // #91: when sharing a visit as a public review AND photos are attached, opt them
  // into the public review too (all-or-nothing). Default on — a user attaching a
  // photo to a public review usually wants it seen; uncheck to keep it private
  // (e.g. a receipt/bill).
  const [sharePhotosPublic, setSharePhotosPublic] = useState(true);
  // #110: a visit can log several dishes (a drink, a shared plate, everyone's
  // order), each with its own rating. On FoodDetail the page's dish is a fixed
  // primary row (rating-only, can't be removed); the user adds more around it.
  // On a restaurant's Log-a-Visit the list starts empty (a dish is optional).
  const [dishes, setDishes] = useState(() => (foodId ? [makeDish({ fixed: true })] : []));
  // #107: photos attached while logging a visit. Staged locally (the visit id
  // doesn't exist yet), then uploaded after the visit is created. Signed-in only
  // for now — guest staging (IndexedDB) is a fast-follow.
  const [visitPhotos, setVisitPhotos] = useState([]); // #115: un-attributed "other" photos (kept private)
  const [photoError, setPhotoError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const { token, isAuthenticated } = useAuth();

  // If the user reviews for the first time while this form is mounted, visitOnly
  // flips true — force visit mode so the now-hidden toggle can't leave us on a
  // review that would 400 as a duplicate (#38).
  useEffect(() => {
    if (visitOnly) setMode('visit');
  }, [visitOnly]);

  // #171: same reasoning as the share checkbox — a standalone public review of a
  // "just for me" restaurant is equally invisible, so review mode is withdrawn
  // and the form logs visits only. Runs on the visibility landing (it arrives
  // with the restaurant fetch, after this form first renders).
  useEffect(() => {
    if (isPrivateForever) setMode('visit');
  }, [isPrivateForever]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Load the restaurant's locations so a multi-location visit can be pinned to one
  // (#41). Restaurant-scoped only — food/menu-item review contexts have no location.
  useEffect(() => {
    if (!restaurantId || isNaN(restaurantId)) {
      setLocations([]);
      return;
    }
    let active = true;
    // #248: owner-aware endpoint — logging a visit to your OWN private restaurant
    // must still offer its locations, so send the token or the picker is empty.
    fetch(`${API_BASE_URL}/restaurants/${restaurantId}/locations/`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => { if (active) setLocations(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setLocations([]); });
    return () => { active = false; };
  }, [restaurantId, API_BASE_URL, token]);

  // #110 multi-dish list helpers. FoodDetail shows a fixed primary (the page's
  // dish) plus any dishes the user adds; a restaurant visit starts empty.
  const showDishSection = mode === 'visit';
  const updateDish = (uid, patch) =>
    setDishes((prev) => prev.map((d) => (d.uid === uid ? { ...d, ...patch } : d)));
  const addDish = () => setDishes((prev) => [...prev, makeDish()]);
  const removeDish = (uid) => setDishes((prev) => {
    const gone = prev.find((d) => d.uid === uid);
    (gone?.photos || []).forEach((p) => URL.revokeObjectURL(p.previewUrl));
    return prev.filter((d) => d.uid !== uid);
  });
  const resetDishes = () => {
    dishes.forEach((d) => (d.photos || []).forEach((p) => URL.revokeObjectURL(p.previewUrl)));
    setDishes(foodId ? [makeDish({ fixed: true })] : []);
  };

  // Resolve one dish row to its payload shape, or null if it names nothing.
  const dishToPayload = (d) => {
    const rating = d.rating || null;
    if (d.fixed) {
      return {
        food_id: foodId ? Number(foodId) : null,
        menu_item_id: menuItemId ? Number(menuItemId) : null,
        dish_name: null,
        rating,
      };
    }
    if (d.menuItemId === '__other__') {
      if (d.matchedFood) {
        // #119: keep the linked catalog food as the category AND the typed name
        // as the display name (custom_name), unless the user left it as-is.
        const typed = (d.dishNameText || '').trim();
        const custom_name = (typed && typed !== d.matchedFood.name) ? typed : null;
        return { food_id: d.matchedFood.id, menu_item_id: null, dish_name: null, custom_name, rating };
      }
      const nm = (d.dishNameText || '').trim();
      return nm ? { food_id: null, menu_item_id: null, dish_name: nm, rating } : null;
    }
    if (d.menuItemId) {
      const mi = menuItems.find((m) => String(m.id) === String(d.menuItemId));
      return { food_id: mi?.food_id || null, menu_item_id: mi ? Number(mi.id) : Number(d.menuItemId), dish_name: null, rating };
    }
    return null; // "Not specified"
  };

  // Dishes that resolve to a real payload, paired with their source uid — in the
  // SAME order the backend assigns positions (#115), so a dish's staged photos can
  // be mapped to the returned visit_dish_id by position. Empty rows drop out.
  const buildDishEntries = () =>
    dishes.map((d) => ({ payload: dishToPayload(d), uid: d.uid })).filter((e) => e.payload);

  // The #110 `dishes` payload (positions match buildDishEntries order).
  const buildDishesPayload = () => buildDishEntries().map((e) => e.payload);

  // Total staged photos across all dishes + the "other" bucket, capped per visit.
  const dishPhotoCount = dishes.reduce((n, d) => n + (d.photos ? d.photos.length : 0), 0);
  const totalStagedPhotos = () => dishPhotoCount + visitPhotos.length;
  const photoRoom = () => Math.max(0, MAX_VISIT_PHOTOS - totalStagedPhotos());

  // #115: stage photos onto a specific dish (preview now, upload after create).
  const addDishPhotos = (uid, files) => {
    if (!files.length) return;
    setPhotoError('');
    setDishes((prev) => {
      const room = MAX_VISIT_PHOTOS - (
        prev.reduce((n, d) => n + (d.photos ? d.photos.length : 0), 0) + visitPhotos.length
      );
      const added = files.slice(0, Math.max(0, room)).map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
      return prev.map((d) => (d.uid === uid ? { ...d, photos: [...(d.photos || []), ...added] } : d));
    });
  };
  const removeDishPhoto = (uid, idx) => {
    setDishes((prev) => prev.map((d) => {
      if (d.uid !== uid) return d;
      const target = (d.photos || [])[idx];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return { ...d, photos: (d.photos || []).filter((_, i) => i !== idx) };
    }));
  };

  // The primary dish's display name for the fixed FoodDetail row.
  const primaryDishLabel = targetName || null;

  // Revoke staged-photo preview object URLs on unmount to avoid leaks (#107/#115).
  useEffect(() => () => {
    visitPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    dishes.forEach((d) => (d.photos || []).forEach((p) => URL.revokeObjectURL(p.previewUrl)));
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // #107: stage "other" (un-attributed, kept private) photos. Shares the per-visit
  // cap with the per-dish photos (#115).
  const addVisitPhotos = (e) => {
    const picked = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';   // allow re-picking the same file
    if (!picked.length) return;
    setPhotoError('');
    setVisitPhotos((prev) => {
      const room = MAX_VISIT_PHOTOS - (
        dishes.reduce((n, d) => n + (d.photos ? d.photos.length : 0), 0) + prev.length
      );
      const added = picked.slice(0, Math.max(0, room)).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      return [...prev, ...added];
    });
  };

  const removeVisitPhoto = (idx) => {
    setVisitPhotos((prev) => {
      const target = prev[idx];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Upload the staged photos to a just-created visit (#115): each dish's photos
  // tagged with that dish's server id (mapped by position from the create
  // response), plus the "other" bucket un-attributed (stays private). Best-effort
  // — the visit is already logged, so a failure is a soft note, not a hard error.
  const uploadStagedPhotos = async (visitId, authToken, responseDishes) => {
    const posToId = {};
    (responseDishes || []).forEach((d) => { posToId[d.position] = d.id; });
    const entries = buildDishEntries();
    let uploadedDishPhoto = false;
    let failed = false;
    for (let i = 0; i < entries.length; i++) {
      const src = dishes.find((d) => d.uid === entries[i].uid);
      const vdid = posToId[i];
      for (const p of (src?.photos || [])) {
        try {
          const jpeg = await prepJpeg(p.file);
          await uploadVisitBlob(API_BASE_URL, authToken, visitId, jpeg, vdid ?? null);
          if (vdid != null) uploadedDishPhoto = true;
        } catch { failed = true; }
      }
    }
    for (const p of visitPhotos) {
      try {
        const jpeg = await prepJpeg(p.file);
        await uploadVisitBlob(API_BASE_URL, authToken, visitId, jpeg);
      } catch { failed = true; }
    }
    return { uploadedDishPhoto, failed };
  };

  const clearVisitPhotos = () => {
    visitPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setVisitPhotos([]);
  };

  // Logged-out: save the review/visit locally as a draft. It posts for real
  // (and clears) when the guest signs in — see useGuestSaves / issue #13.
  const saveGuestDraft = async () => {
    const target = {
      name: targetName || null,
      restaurant_id: restaurantId || null,
      food_id: foodId || null,
    };
    if (mode === 'review') {
      addGuestDraft('reviews', {
        rating,
        title: title || null,
        review_text: reviewText || null,
        restaurant_id: restaurantId || null,
        food_id: foodId || null,
        menu_item_id: menuItemId || null,
        target,
      });
      setSuccess('Saved as a draft on this device — it will post automatically when you sign in.');
      setTitle('');
      setReviewText('');
      setRating(5);
    } else {
      // #107/#115: stash picked photos in IndexedDB (localStorage can't hold blobs)
      // keyed to this draft, each tagged with its dish position (null = "other"),
      // so the guest→account merge can re-attribute them. Prep now so metadata is
      // stripped before storage. Positions match the dishes payload below.
      const entries = buildDishEntries();
      let photos = [];
      try {
        for (let i = 0; i < entries.length; i++) {
          const src = dishes.find((d) => d.uid === entries[i].uid);
          for (const p of (src?.photos || [])) {
            photos.push({ position: i, blob: await prepJpeg(p.file) });
          }
        }
        for (const p of visitPhotos) {
          photos.push({ position: null, blob: await prepJpeg(p.file) });
        }
      } catch {
        photos = [];
      }
      const stored = addGuestDraft('visits', {
        restaurant_id: restaurantId,
        location_id: locationId ? Number(locationId) : null,
        // #110: carry the full dish list through the guest→account merge.
        dishes: entries.map((e) => e.payload),
        visit_date: new Date(visitDate).toISOString(),
        meal_type: mealType || null,
        rating,
        notes: reviewText || null,
        create_public_review: createPublicReview,
        photo_count: photos.length,
        target,
      });
      if (photos.length > 0) {
        await saveGuestPhotos(stored.draft_id, photos);
      }
      setSuccess(
        photos.length > 0
          ? 'Visit saved as a draft on this device — it and your photos will post when you sign in.'
          : 'Visit saved as a draft on this device — it will post when you sign in.'
      );
      clearVisitPhotos();
      setVisitDate(toLocalDatetimeInputValue());
      setMealType('');
      setLocationId('');
      resetDishes();
      setReviewText('');
      setRating(5);
      setCreatePublicReview(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate that at least one ID is provided and valid
    const hasValidId = (restaurantId && !isNaN(restaurantId)) ||
                       (foodId && !isNaN(foodId)) ||
                       (menuItemId && !isNaN(menuItemId));

    if (!hasValidId) {
      setError(`Unable to ${mode === 'review' ? 'submit review' : 'log visit'}: missing restaurant, food, or menu item ID`);
      console.error('Form error - IDs:', { restaurantId, foodId, menuItemId });
      return;
    }

    // Guests keep their content locally until they have an account.
    if (!isAuthenticated) {
      await saveGuestDraft();
      return;
    }

    setLoading(true);

    try {
      if (mode === 'review') {
        // Submit as review
        const reviewData = {
          rating,
          title: title || null,
          review_text: reviewText || null,
          restaurant_id: restaurantId || null,
          food_id: foodId || null,
          menu_item_id: menuItemId || null,
        };

        const response = await fetch(`${API_BASE_URL}/reviews/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(reviewData),
        });

        if (!response.ok) {
          let errorMessage = 'Failed to submit review';
          try {
            const errorData = await response.json();
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
            } else {
              errorMessage = JSON.stringify(errorData.detail || errorData);
            }
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setSuccess('Review submitted successfully!');
        setTitle('');
        setReviewText('');
        setRating(5);

        if (onReviewSubmitted) {
          onReviewSubmitted(data);
        }
      } else {
        // Submit as visit
        const visitData = {
          restaurant_id: restaurantId,
          location_id: locationId ? Number(locationId) : null,
          // #110: the dishes logged on this visit (each menu item / catalog food /
          // off-menu name + its own rating). The backend mirrors dishes[0] onto
          // the legacy singular columns for back-compat.
          dishes: buildDishesPayload(),
          visit_date: new Date(visitDate).toISOString(),
          meal_type: mealType || null,
          rating: rating,  // visit (overall experience) rating
          notes: reviewText || null,
          create_public_review: createPublicReview,
        };

        const response = await fetch(`${API_BASE_URL}/api/v1/visits/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(visitData),
        });

        if (!response.ok) {
          let errorMessage = 'Failed to log visit';
          try {
            const errorData = await response.json();
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
            } else {
              errorMessage = JSON.stringify(errorData.detail || errorData);
            }
          } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        // #107/#115: upload staged photos to the new visit, per dish + "other"
        // bucket (best-effort — the visit is already logged).
        if (totalStagedPhotos() > 0 && data?.id) {
          const { uploadedDishPhoto, failed } = await uploadStagedPhotos(data.id, token, data.dishes);
          if (failed) {
            setPhotoError('Visit logged, but a photo didn’t upload. You can add photos to it later from your profile.');
          }
          // #91/#115: dish photos upload AFTER the public review is created, so opt
          // them in now. dish_photos_only keeps the "other" bucket private.
          if (uploadedDishPhoto && createPublicReview && sharePhotosPublic) {
            try {
              await fetch(`${API_BASE_URL}/api/v1/visits/${data.id}/share-photos?share=true&dish_photos_only=true`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
              });
            } catch {
              setPhotoError('Visit shared, but your photos couldn’t be made public. You can retry from your profile.');
            }
          }
          clearVisitPhotos();
        }
        setSuccess(createPublicReview ? 'Visit logged and shared as public review!' : 'Visit logged successfully!');
        setVisitDate(toLocalDatetimeInputValue());
        setMealType('');
        setLocationId('');
        resetDishes();
        setReviewText('');
        setRating(5);
        setCreatePublicReview(false);

        if (onReviewSubmitted) {
          onReviewSubmitted(data);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="star-rating mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= (hoveredStar || rating) ? 'filled' : ''}`}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            style={{
              cursor: 'pointer',
              fontSize: '2rem',
              color: star <= (hoveredStar || rating) ? '#ffc107' : '#e4e5e9',
              marginRight: '0.25rem',
            }}
          >
            ★
          </span>
        ))}
        <span className="ms-2 text-muted">({rating} star{rating !== 1 ? 's' : ''})</span>
      </div>
    );
  };

  return (
    <div className="review-form">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">{mode === 'review' ? 'Write a Review' : 'Log a Visit'}</h4>
        {/* #171: no Review/Visit switch on a private "just for me" place — a public
            review of it has nowhere to appear. Visit logging still works. */}
        {!visitOnly && !isPrivateForever && (
          <ButtonGroup size="sm">
            <Button
              variant={mode === 'review' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('review')}
            >
              Review
            </Button>
            <Button
              variant={mode === 'visit' ? 'primary' : 'outline-primary'}
              onClick={() => setMode('visit')}
            >
              Visit
            </Button>
          </ButtonGroup>
        )}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      {photoError && <Alert variant="warning" dismissible onClose={() => setPhotoError('')}>{photoError}</Alert>}

      {!isAuthenticated && (
        <Alert variant="info" className="py-2 small">
          You're browsing as a guest — this is saved on this device as a draft and
          posts automatically when you <a href="/login">sign in</a>.
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        {/* Visit-specific fields */}
        {mode === 'visit' && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>When did you visit?</Form.Label>
              <Form.Control
                type="datetime-local"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                required
              />
            </Form.Group>

            {locations.length > 1 && (
              <Form.Group className="mb-3">
                <Form.Label>Which location? (Optional)</Form.Label>
                <Form.Select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  <option value="">Not specified</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{locationLabel(loc)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* #110: which dishes did you have? A visit can log several — a drink,
                a shared plate, everyone's order — each with its own rating. On
                FoodDetail the page's dish is a fixed primary; the user adds more. */}
            {showDishSection && (
              <Form.Group className="mb-3">
                <Form.Label>{foodId ? 'Dishes' : 'Dishes (Optional)'}</Form.Label>
                {dishes.map((d, i) => (
                  <VisitDishRow
                    key={d.uid}
                    index={i}
                    dish={d}
                    menuItems={menuItems}
                    apiBaseUrl={API_BASE_URL}
                    fixed={!!d.fixed}
                    fixedLabel={d.fixed ? primaryDishLabel : ''}
                    onChange={(patch) => updateDish(d.uid, patch)}
                    onRemove={d.fixed ? null : () => removeDish(d.uid)}
                    onAddPhotos={(files) => addDishPhotos(d.uid, files)}
                    onRemovePhoto={(idx) => removeDishPhoto(d.uid, idx)}
                    photoRoom={photoRoom()}
                  />
                ))}
                <Button type="button" variant="outline-secondary" size="sm" onClick={addDish}>
                  + Add {dishes.length > 0 ? 'another ' : 'a '}dish
                </Button>
                <Form.Text className="d-block text-muted mt-1">
                  Rate each dish for what the food was like — kept separate from your overall experience below.
                  Add a photo to a dish so it shows on that dish's review.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Meal Type (Optional)</Form.Label>
              <Form.Select
                value={mealType}
                onChange={(e) => setMealType(e.target.value)}
              >
                <option value="">Select meal type...</option>
                {mealTypes.map((m) => (
                  <option key={m.slug} value={m.slug}>{m.display_name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* #115: "other" photos — not tied to a dish (storefront, receipt, the
                table). These are kept PRIVATE: only dish photos can go public. */}
            <Form.Group className="mb-3">
                <Form.Label>Other photos (Optional)</Form.Label>
                {visitPhotos.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    {visitPhotos.map((p, idx) => (
                      <div key={p.previewUrl} className="position-relative">
                        <img
                          src={p.previewUrl}
                          alt={`Selected ${idx + 1}`}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                        />
                        <button
                          type="button"
                          className="btn-close bg-white rounded-circle position-absolute top-0 end-0 m-1 p-1"
                          aria-label="Remove photo"
                          style={{ fontSize: '0.55rem' }}
                          onClick={() => removeVisitPhoto(idx)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {photoRoom() > 0 && (
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={addVisitPhotos}
                  />
                )}
                <Form.Text className="text-muted">
                  Kept private — for a shot you don't want public (a receipt, the room). Up to {MAX_VISIT_PHOTOS} photos per visit total (dishes + other). Location and other metadata are removed before upload.
                  {!isAuthenticated && ' Photos are kept on this device and upload when you sign in.'}
                </Form.Text>
              </Form.Group>
          </>
        )}

        {/* Rating (common). In visit mode this is the OVERALL experience —
            service, ambience, value — distinct from the per-dish ratings (#110). */}
        <Form.Group className="mb-3">
          <Form.Label>{mode === 'visit' ? 'Overall experience' : 'Your Rating'}</Form.Label>
          {mode === 'visit' && (
            <Form.Text className="d-block text-muted mb-1">
              How was the visit overall — service, ambience, value?
            </Form.Text>
          )}
          {renderStars()}
        </Form.Group>

        {/* Review-specific title */}
        {mode === 'review' && (
          <Form.Group className="mb-3">
            <Form.Label>Review Title (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sum up your experience"
              maxLength={200}
            />
          </Form.Group>
        )}

        {/* Text field (review text or visit notes) */}
        <Form.Group className="mb-3">
          <Form.Label>{mode === 'review' ? 'Your Review' : 'Notes'} (Optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder={mode === 'review' ? 'Share details about your experience...' : 'What did you order? How was the service?'}
          />
        </Form.Group>

        {/* Visit: Share as public review checkbox. Hidden entirely for a private
            "just for me" restaurant (#171) — there's no public page for the review
            to appear on, so offering it only produces a review nobody can see. */}
        {mode === 'visit' && isPrivateForever && (
          <Form.Text className="text-muted d-block mb-3">
            🔒 This place is private to you, so visits here stay private — there's no public page for a review to appear on.
          </Form.Text>
        )}
        {mode === 'visit' && !isPrivateForever && (
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="create-public-review"
              label="📢 Share as public review"
              checked={createPublicReview}
              onChange={(e) => setCreatePublicReview(e.target.checked)}
            />
            <Form.Text className="text-muted">
              {isAwaitingApproval
                ? "This place is still awaiting approval — your review will appear on its page once it's approved."
                : "If checked, your visit will also appear as a public review on this restaurant's page."}
            </Form.Text>
            {/* #91/#115: opt the DISH photos into the public review (all-or-nothing).
                The "other" bucket is always private, so it's not offered here. */}
            {createPublicReview && dishPhotoCount > 0 && (
              <div className="mt-2 ps-3">
                <Form.Check
                  type="checkbox"
                  id="share-visit-photos"
                  label={`📷 Include your ${dishPhotoCount} dish photo${dishPhotoCount === 1 ? '' : 's'} in the public review`}
                  checked={sharePhotosPublic}
                  onChange={(e) => setSharePhotosPublic(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  Uncheck to keep them private for now. Public photos can be reported. Your "other" photos always stay private.
                </Form.Text>
              </div>
            )}
          </Form.Group>
        )}

        <Button variant="primary" type="submit" disabled={loading}>
          {loading
            ? 'Submitting...'
            : !isAuthenticated
              ? (mode === 'review' ? 'Save review draft' : 'Save visit draft')
              : (mode === 'review' ? 'Submit Review' : 'Log Visit')}
        </Button>
      </Form>
    </div>
  );
}

export default ReviewForm;
